import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setGtmPreviousUserChoice, GTM_EVENTS, setGtmEventChange } from '../gtm/gtmSlice';
import { FULFILMENT_TYPES, JOURNEY_TYPES } from '@/constants/constants';
import { logger } from '@/services/logger/logger';
import { setProvider, initOrderReference } from '@/features/paymentSelect/paymentSelectSlice';

import journeysData from './journeys.json';
import { getCurrentJourney, loadJourney } from './utils';
import { useCartContext } from '../cart/CartContext';

export function useJourneyStartNavigate() {
  const journeysState = useAppSelector(state => state.journeys);
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const { getCartFulfilmentType, cartData, cartId } = useCartContext();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const journeyStartNavigate = useCallback(() => {
    async function doJourneyStartNavigate() {
      try {
        const { amount, currentMoneybuyPosition } = paymentSelectState;
        const thisJourneyName = getCurrentJourney(journeysState);
        const { basePath, basePathAdopt } = loadJourney(journeysData, thisJourneyName);

        const isAARN = getCartFulfilmentType() === FULFILMENT_TYPES.AARN;

        // Tweak the navigate function params dynamically based on the context:
        const thisBasePath = isAARN && basePathAdopt ? basePathAdopt : basePath;

        // Initialise before navigating so StaleJourneyContainer sees an active session
        dispatch(initOrderReference());

        // For AARN carts in monthly journey, skip payment-select and go directly to user-details
        if (isAARN) {
          // Set GOCARDLESS as the provider, and initialise order reference, for AARN journeys to satisfy validation. Without this, you get stuck in horrible loops
          dispatch(setProvider('GOCARDLESS'));
          void navigate(`${thisBasePath}user-details`);
        } else {
          void navigate(thisBasePath);
        }

        // Nix this happening for Payin; that has its OWN similiar functionality on the Audience page:
        if (thisJourneyName !== JOURNEY_TYPES.PAYIN && cartId && cartData) {
          // Trigger the 'Giftaid Page' GTM event, where any
          // 'previousUserChoice' comparison logic will be carried out:
          dispatch(setGtmEventChange({ eventType: GTM_EVENTS.GIFTAID_PAGE, cartId, cartData }));

          // THEN, update our previousUserChoice values to keep it in sync,
          // ensuring any subsequent re-selection will work in the same way:
          dispatch(setGtmPreviousUserChoice({
            previousAmount: amount,
            previousPosition: currentMoneybuyPosition,
            previousJourney: thisJourneyName
          }));
        }
      } catch (error) {
        logger.error('Error while navigating to step...');
        logger.error(error);
        await navigate('/not-found');
      }
    }

    void doJourneyStartNavigate();
  }, [journeysState, navigate, dispatch, paymentSelectState, getCartFulfilmentType, cartData, cartId]);

  return journeyStartNavigate;
}
